<div class="add-order-dialog">
  <!-- Fixed Header with Step Indicator and Error Messages -->
  <div class="fixed-header">
    <!-- Dialog Title Section -->
    <div class="dialog-title-section">
      <div class="flex justify-between items-start">
        <div>
          <h1 class="text-xl font-medium text-surface-on">
            Add New Order
          </h1>
          <p class="text-sm mt-2 text-surface-on">Create a new order for a customer</p>
        </div>
        <button type="button" class="close-button" (click)="closeDialog()" aria-label="Close dialog">
          <mat-icon svgIcon="close" class="w-6 h-6"></mat-icon>
        </button>
      </div>
    </div>

    <!-- Fixed Error Messages -->
    <div class="error-messages-fixed">
      @if (stepValidationErrors().length > 0) {
      <div class="error-message text-error px-4 py-3 text-sm bg-error-50 border border-error-200 rounded-lg shadow-sm">
        <div class="flex items-start gap-2">
          <mat-icon svgIcon="alert-circle" class="w-4 h-4 flex-shrink-0 mt-0.5"></mat-icon>
          <div class="space-y-1">
            @for (error of stepValidationErrors(); track error) {
            <div>{{ error }}</div>
            }
          </div>
        </div>
      </div>
      }
    </div>
  </div>

  <!-- Scrollable Content Area with Separate Containers -->
  <div class="scrollable-content" #scrollContainer>
    <cac-dialog-layout [title]="''" [subtitle]="''" [hideControls]="false" [submitBtn]="canSave() ? 'Create' : 'Next'"
      (onSubmit)="onMainAction($event)">
      <!-- Step Content -->
      <div class="step-content-wrapper">

        <!-- Step 1: Customer Information -->
        @if (currentStep() === 0) {
        <div class="step-section">
          <h2 class="text-xl font-semibold mb-6 text-gray-900">Customer Information</h2>

          <div class="border rounded-lg shadow-sm border-gray-200 p-6 mb-6">
            <!-- Customer Selection -->
            <div class="space-y-2 mb-6">
              <label class="block text-sm font-medium text-gray-700">Select Customer (Optional)</label>
              <app-lazy-dropdown [multiple]="false" [searchable]="true" [clearable]="true"
                placeholder="Search and select a customer..."
                hint="You can leave this empty to create an order for a new customer" [apiAdapter]="customerApiAdapter"
                [itemMapper]="customerItemMapper" idsFilterKey="CustomerIds" searchField="email"
                duplicateMessage="Customer is already selected" (selectionChange)="onCustomerSelectionChange($event)" />
            </div>

            <!-- Customer Details Form -->
            <div class="p-4 rounded-lg mb-6">
              <h4 class="text-md font-medium mb-3">Customer Details</h4>
              @if (loadingCustomerDetails()) {
              <div class="flex items-center justify-center py-4">
                <div class="text-blue-600">Loading customer details...</div>
              </div>
              }
              <div class="grid grid-cols-2 gap-3">
                <cac-field [control]="customerForm.controls.customerFullName" label="Full Name" />
                <cac-field [control]="customerForm.controls.customerEmail" label="Email" type="email" />
                <div class="col-span-2 grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3 items-start" dir="ltr">
                  <lib-country-code-field [control]="countryCode" />
                  <cac-field [control]="customerForm.controls.customerPhoneNumber" label="Phone Number" type="tel" />
                </div>
              </div>
            </div>

            <!-- Address Selection (only show if customer is selected) -->
            @if (selectedCustomer$.value) {
            <div class="space-y-2 mb-6">
              <label class="block text-sm font-medium text-gray-700">Select Address (Optional)</label>
              @if (loadingCustomerAddresses()) {
              <div class="flex items-center justify-center py-4">
                <div class="text-blue-600">Loading customer addresses...</div>
              </div>
              } @else if (customerAddresses$.value.length > 0) {
              <select class="form-select w-full p-2 border border-gray-300 rounded-md"
                (change)="onAddressSelectionChange($event)">
                <option value="">Choose an existing address or enter new one below</option>
                @for (address of customerAddresses$.value; track address.id) {
                <option [value]="address.id">{{ getAddressDisplayText(address) }}</option>
                }
              </select>
              } @else {
              <div class="text-sm text-gray-500">No saved addresses found for this customer.</div>
              }
            </div>
            }

            <!-- Shipping Address Form -->
            <div class="p-4 rounded-lg">
              <h4 class="text-md font-medium mb-3">Shipping Address</h4>
              <cac-form-builder [options]="addressForm" />
            </div>
          </div>
        </div>
        }

        <!-- Step 2: Items -->
        @if (currentStep() === 1) {
        <div class="step-section">
          <h2 class="text-xl font-semibold mb-6 text-gray-900">Order Items</h2>

          <!-- Product Search -->
          <div class="border rounded-lg shadow-sm border-gray-200 p-6 mb-6">
            <div class="space-y-2 mb-6">
              <label class="block text-sm font-medium text-gray-700">Add Products</label>
              <app-lazy-dropdown [multiple]="false" [searchable]="true" [clearable]="true" [clearOnSelection]="true"
                placeholder="Search and select products to add..."
                hint="Search by product name to add items to the order. You can add the same product multiple times with different variants."
                [apiAdapter]="productApiAdapter" [itemMapper]="productItemMapper" idsFilterKey="ProductIds"
                searchField="EnglishName" (selectionChange)="onProductSelectionChange($event)" />
            </div>
          </div>

          <!-- Order Items List -->
          @if (orderItems().length > 0) {
          <div class="space-y-4">
            @for (orderItem of orderItems(); track $index; let i = $index) {
            <div class="border rounded-lg shadow-sm border-gray-200 p-6" [class.border-red-300]="orderItem.outOfStock"
              [class.bg-red-50]="orderItem.outOfStock">

              <!-- Loading State -->
              @if (orderItem.loadingProduct) {
              <div class="flex items-center justify-center py-8">
                <div class="text-blue-600 flex items-center gap-2">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  Loading product details...
                </div>
              </div>
              }

              <!-- Product Details -->
              @if (!orderItem.loadingProduct && orderItem.product) {
              <div class="space-y-4">
                <!-- Product Header -->
                <div class="flex items-start justify-between">
                  <div class="flex items-start gap-4">
                    <!-- Product Image -->
                    @if (orderItem.product.images.length > 0) {
                    <img [src]="orderItem.product.images[0].url"
                      class="w-16 h-16 object-cover rounded-lg border border-gray-200">
                    } 

                    <!-- Product Info -->
                    <div>
                      <h3 class="font-medium text-gray-900">
                        {{ orderItem.product.name.english || orderItem.product.name.arabic }}
                      </h3>
                      @if (orderItem.product.name.arabic && orderItem.product.name.english !==
                      orderItem.product.name.arabic) {
                      <p class="text-sm text-gray-600">{{ orderItem.product.name.arabic }}</p>
                      }
                      @if (orderItem.product.model) {
                      <p class="text-sm text-gray-500">Model: {{ orderItem.product.model }}</p>
                      }
                    </div>
                  </div>

                  <!-- Remove Button -->
                  <button type="button" (click)="removeOrderItem(i)" class="text-red-600 hover:text-red-800 p-1">
                    <mat-icon svgIcon="close" class="w-5 h-5"></mat-icon>
                  </button>
                </div>

                <!-- Out of Stock Warning -->
                @if (orderItem.outOfStock) {
                <div class="bg-red-100 border border-red-300 text-red-700 px-3 py-2 rounded-md text-sm">
                  <div class="flex items-center gap-2">
                    <span>This product is out of stock. Please remove it from the order.</span>
                  </div>
                </div>
                }

                <!-- Variant Selection (for variable products) -->
                @if (orderItem.product.type === 1 && orderItem.product.variants.length > 1) {
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Select Variant</label>
                  @if (getAvailableVariants(orderItem).length > 0) {
                  <select class="form-select w-full p-2 border border-gray-300 rounded-md"
                    [value]="orderItem.selectedVariant?.id || ''"
                    (change)="onVariantSelectionChange(orderItem, +$any($event.target).value)">
                    <option value="">Choose a variant...</option>
                    @for (variant of getAvailableVariants(orderItem); track variant.id) {
                    <option [value]="variant.id">
                      {{ getVariantDisplayText(variant) }}
                      @if (variant.isManageableStock) {
                      - Stock: {{ variant.stock }}
                      }
                    </option>
                    }
                  </select>
                  } @else {
                  <div class="p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
                    <div class="flex items-center gap-2">
                      <mat-icon svgIcon="alert-circle" class="w-4 h-4"></mat-icon>
                      <span>All variants of this product are already selected in other order items.</span>
                    </div>
                  </div>
                  }
                </div>
                }

                <!-- Quantity and Price Fields -->
                @if (orderItem.selectedVariant || (orderItem.product && orderItem.product.type !== 1)) {
                <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <!-- Quantity -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Quantity</label>
                    <input type="number" class="form-input w-full p-2 border border-gray-300 rounded-md"
                      [class.border-red-300]="orderItem.form.controls.quantity.invalid && orderItem.form.controls.quantity.touched"
                      [formControl]="orderItem.form.controls.quantity" min="1">

                    <!-- Stock Info (informational only) -->
                    @if (getCurrentVariant(orderItem)?.isManageableStock) {
                    <div class="text-sm text-blue-600">
                      Available stock: {{ getCurrentVariant(orderItem)!.stock }}
                    </div>
                    }

                    <!-- Validation Error -->
                    @if (orderItem.form.controls.quantity.invalid && orderItem.form.controls.quantity.touched) {
                    <div class="text-sm text-red-600">
                      @if (orderItem.form.controls.quantity.hasError('required')) {
                      Quantity is required
                      } @else if (orderItem.form.controls.quantity.hasError('min')) {
                      Minimum quantity is 1
                      }
                    </div>
                    }
                  </div>

                  <!-- Item Price -->
                  <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-700">Item Price</label>
                    <input type="text" class="form-input w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                      [value]="getItemPrice(orderItem) | kwdCurrency" readonly>
                    @if (getCurrentVariant(orderItem)?.salePrice && getCurrentVariant(orderItem)!.salePrice! <
                      getCurrentVariant(orderItem)!.regularPrice) { <div class="text-sm text-gray-500 line-through">
                      {{ getCurrentVariant(orderItem)!.regularPrice | kwdCurrency }}
                  </div>
                  }
                </div>

                <!-- Total Price -->
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">Total Price</label>
                  <input type="text"
                    class="form-input w-full p-2 border border-gray-300 rounded-md bg-gray-50 font-medium"
                    [value]="getTotalPrice(orderItem) | kwdCurrency" readonly>
                </div>

                <!-- Spacer -->
                <div></div>
              </div>
              }
            </div>
            }
          </div>
          }
        </div>
        } @else {
        <div class="text-center py-8 text-gray-500">
          <mat-icon svgIcon="shopping-cart" class="w-12 h-12 mx-auto mb-2 text-gray-400"></mat-icon>
          <p>No items added yet. Use the search above to add products to the order.</p>
        </div>
        }
      </div>
      }

      <!-- Step 3: Order Summary -->
      @if (currentStep() === 2) {
      <div class="step-section">
        <h2 class="text-xl font-semibold mb-6 text-gray-900">Order Summary</h2>

        @if (loadingOrderSummary()) {
        <div class="flex items-center justify-center py-8">
          <div class="text-blue-600 flex items-center gap-2">
            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            Loading order summary...
          </div>
        </div>
        } @else if (orderSummary()) {

        <!-- Payment Information & Pricing Section -->
        <div class="border rounded-lg shadow-sm border-gray-200 p-6">
          <h3 class="text-lg font-semibold mb-4 text-gray-900">Payment Information & Pricing</h3>

          <!-- Payment Method Selection -->
          <div class="mb-6">
            <cac-field
              [control]="paymentMethodControl"
              label="Payment Method"
              controlType="select"
              [items]="getPaymentMethodDisplayOptions()"
              [searchable]="false"
              [clearable]="false"
            ></cac-field>
          </div>

          <!-- Pricing Breakdown -->
          <div class="border-t border-gray-200 pt-4">
            <h4 class="text-base font-medium mb-3 text-gray-900">Pricing Breakdown</h4>
            <div class="space-y-3">
              <div class="flex justify-between">
                <span class="text-sm text-gray-600">Items Price:</span>
                <span class="text-sm font-medium text-gray-900">{{ orderSummary()!.prices.itemsPrice | kwdCurrency }}</span>
              </div>

              @if (orderSummary()!.prices.itemsDiscount && orderSummary()!.prices.itemsDiscount > 0) {
              <div class="flex justify-between text-green-600">
                <span class="text-sm">Items Discount:</span>
                <span class="text-sm font-medium">-{{ orderSummary()!.prices.itemsDiscount | kwdCurrency }}</span>
              </div>
              }

              <div class="flex justify-between">
                <span class="text-sm text-gray-600">Shipping Fee:</span>
                <span class="text-sm font-medium text-gray-900">{{ orderSummary()!.prices.shippingFee | kwdCurrency }}</span>
              </div>

              <div class="flex justify-between" [class.text-green-600]="orderSummary()!.prices.shippingFeeDiscount > 0">
                <span class="text-sm" [class.text-gray-600]="orderSummary()!.prices.shippingFeeDiscount === 0">Shipping Fee Discount:</span>
                <span class="text-sm font-medium">{{ orderSummary()!.prices.shippingFeeDiscount > 0 ? '-' : '' }}{{ orderSummary()!.prices.shippingFeeDiscount | kwdCurrency }}</span>
              </div>

              @if (orderSummary()!.prices.totalCouponCodeDiscount && orderSummary()!.prices.totalCouponCodeDiscount > 0) {
              <div class="flex justify-between text-green-600">
                <span class="text-sm">Coupon Code Discount:</span>
                <span class="text-sm font-medium">-{{ orderSummary()!.prices.totalCouponCodeDiscount | kwdCurrency }}</span>
              </div>
              }

              <hr class="border-gray-300">

              <div class="flex justify-between text-lg font-semibold text-gray-900">
                <span>Total Order Price:</span>
                <span>{{ orderSummary()!.prices.totalOrderPrice | kwdCurrency }}</span>
              </div>
            </div>
          </div>
        </div>
        }
      </div>
      }

  </div>

  <!-- Previous Button in Bottom Slot -->
  <div class="navigation-controls flex justify-start items-center w-full" slot="bottom">
    @if (canGoToPreviousStep()) {
    <cac-button (click)="previousStep()" appearance="stroked" icon="chevron-left">
      Previous
    </cac-button>
    } @else {
    <cac-button (click)="closeDialog()" appearance="stroked">
      Cancel
    </cac-button>
    }
  </div>
  </cac-dialog-layout>
</div>
